import type { CSSProperties, ReactNode } from 'react';

/**
 * Shared card primitive. Every content card in the app (Knowledge, Milestones,
 * Activities, Solids prep, baby info) should use Card + CardHeader + CardBody
 * + CardFooter so spacing and typography stay consistent.
 *
 *   <Card>
 *     <CardHeader emoji="📊" title="..." badge={...} />
 *     <CardBody>...</CardBody>
 *     <CardFooter>...</CardFooter>
 *   </Card>
 */

const BADGE_VARIANTS: Record<string, CSSProperties> = {
  orange: { background: 'hsl(var(--feed-bg))',  color: 'hsl(var(--feed-accent))' },
  purple: { background: 'hsl(var(--sleep-bg))', color: 'hsl(var(--sleep-accent))' },
  green:  { background: 'hsl(150,50%,93%)',     color: 'hsl(150,50%,32%)' },
  blue:   { background: 'hsl(var(--primary) / 0.1)', color: 'hsl(var(--primary))' },
  yellow: { background: 'hsl(45,90%,92%)',      color: 'hsl(35,80%,35%)' },
};

export type BadgeVariant = keyof typeof BADGE_VARIANTS;

interface CardProps {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  onClick?: () => void;
}

export const Card = ({ children, className = '', style, onClick }: CardProps) => (
  <div
    onClick={onClick}
    className={`bg-white rounded-3xl p-4 ${onClick ? 'active:scale-[0.99] transition-transform' : ''} ${className}`}
    style={{ boxShadow: 'var(--shadow-card)', ...style }}
  >
    {children}
  </div>
);

interface CardHeaderProps {
  emoji?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  badge?: { label: string; variant?: BadgeVariant };
  trailing?: ReactNode;
}

export const CardHeader = ({ emoji, title, description, badge, trailing }: CardHeaderProps) => (
  <div className="flex items-start gap-2.5">
    {emoji && (
      <span className="text-2xl leading-none flex-shrink-0 mt-0.5">{emoji}</span>
    )}
    <div className="flex-1 min-w-0">
      <p className="font-bold text-foreground" style={{ fontSize: 15, lineHeight: '21px' }}>{title}</p>
      {description && (
        <p className="text-muted-foreground mt-1" style={{ fontSize: 12, lineHeight: '18px' }}>{description}</p>
      )}
    </div>
    {badge && (
      <span
        className="flex-shrink-0 px-2.5 py-1 rounded-full"
        style={{ fontSize: 11, fontWeight: 500, ...(BADGE_VARIANTS[badge.variant ?? 'blue']) }}
      >
        {badge.label}
      </span>
    )}
    {trailing}
  </div>
);

interface CardBodyProps {
  children: ReactNode;
  /** Indent body text to align under the header title (past the emoji column). */
  indent?: boolean;
  className?: string;
}

export const CardBody = ({ children, indent = false, className = '' }: CardBodyProps) => (
  <div
    className={`mt-2 ${indent ? 'ml-[42px]' : ''} ${className}`}
    style={{ fontSize: 12, lineHeight: '18px', color: 'hsl(var(--muted-foreground))' }}
  >
    {children}
  </div>
);

interface CardFooterProps {
  children: ReactNode;
  /** When true, gap between buttons is 8px (default) — set wider for full-width single button. */
  className?: string;
}

export const CardFooter = ({ children, className = '' }: CardFooterProps) => (
  <div className={`flex gap-2 mt-3 ${className}`}>{children}</div>
);

/**
 * Pre-styled brand pill (small uppercase label inside a coloured background).
 * Used for "PREP FOR SOLIDS" / "5m · Formula" type labels above a card title.
 */
interface CardPillProps {
  label: string;
  variant?: BadgeVariant;
  className?: string;
}

export const CardPill = ({ label, variant = 'orange', className = '' }: CardPillProps) => (
  <span
    className={`inline-flex items-center rounded-full px-2.5 py-1 ${className}`}
    style={{ fontSize: 11, fontWeight: 500, ...BADGE_VARIANTS[variant] }}
  >
    {label}
  </span>
);
